import { GOOGLE_API_KEY } from '../config';

/**
 * Normalize geofence from either old flat format [{lat,lng},...]
 * or new multi-polygon format [[{lat,lng},...],...]
 * Always returns [[{lat,lng},...], ...] (array of polygon arrays).
 */
export function normalizeGeofence(geofence) {
    if (!Array.isArray(geofence) || geofence.length === 0) return [];
    if (Array.isArray(geofence[0])) {
        return geofence.filter(p => Array.isArray(p) && p.length >= 3);
    }
    if (typeof geofence[0]?.lat === 'number') {
        return geofence.length >= 3 ? [geofence] : [];
    }
    return [];
}

/** True when normalized polygon rings match (lat/lng per vertex). */
export function geofencesEqual(a, b) {
    const na = normalizeGeofence(a);
    const nb = normalizeGeofence(b);
    if (na.length !== nb.length) return false;
    return na.every((poly, i) => {
        const other = nb[i];
        if (!other || poly.length !== other.length) return false;
        return poly.every((p, j) => p.lat === other[j].lat && p.lng === other[j].lng);
    });
}

/**
 * Build a Google Static Maps URL for a geofence (single or multi-polygon).
 * Uses the overall bounding box across all polygons for the visible area.
 * Each polygon becomes a separate path= parameter.
 */
export function buildGeofenceStaticMapUrl(geofence, width = 160, height = 110, opts = {}) {
    if (!GOOGLE_API_KEY) return null;

    const polygons = normalizeGeofence(geofence);
    if (polygons.length === 0) return null;

    const { ingress = [], egress = [] } = opts;

    const allPoints = polygons.flat();
    const markerPoints = [
        ...(Array.isArray(ingress) ? ingress : []),
        ...(Array.isArray(egress) ? egress : []),
    ];
    const lats = [...allPoints.map(p => p.lat), ...markerPoints.map(p => p.lat)];
    const lngs = [...allPoints.map(p => p.lng), ...markerPoints.map(p => p.lng)];
    const north = Math.max(...lats), south = Math.min(...lats);
    const east  = Math.max(...lngs), west  = Math.min(...lngs);

    const latPad = (north - south) * 0.02;
    const lngPad = (east  - west)  * 0.02;
    const visible = [
        `${south - latPad},${west - lngPad}`,
        `${north + latPad},${east + lngPad}`,
    ].join('|');

    const pathParams = polygons
        .map(poly => {
            const pts = poly.map(p => `${p.lat},${p.lng}`).join('|');
            const pathValue = `color:0x2254A3FF|weight:2|fillcolor:0x1E52A355|${pts}`;
            return `path=${encodeURIComponent(pathValue)}`;
        })
        .join('&');

    const markerParams = [];
    if (ingress.length > 0) {
        const m = ingress.map(p => `${p.lat},${p.lng}`).join('|');
        markerParams.push(`markers=${encodeURIComponent(`color:green|label:E|${m}`)}`);
    }
    if (egress.length > 0) {
        const m = egress.map(p => `${p.lat},${p.lng}`).join('|');
        markerParams.push(`markers=${encodeURIComponent(`color:red|label:X|${m}`)}`);
    }

    return (
        `https://maps.googleapis.com/maps/api/staticmap` +
        `?maptype=satellite` +
        `&size=${width}x${height}` +
        `&scale=2` +
        `&visible=${encodeURIComponent(visible)}` +
        `&${pathParams}` +
        (markerParams.length ? `&${markerParams.join('&')}` : '') +
        `&key=${GOOGLE_API_KEY}`
    );
}

/**
 * Total vertex count across all polygons in a normalized geofence.
 * Returns 0 for empty / invalid input.
 */
export function countVertices(geofence) {
    const polygons = normalizeGeofence(geofence);
    return polygons.reduce((sum, poly) => sum + poly.length, 0);
}

/**
 * Total area in acres across all polygons. Equirectangular projection at the
 * mean latitude — good enough at property scale (<1 km extent).
 * Returns null when input is empty / invalid.
 */
export function computeAcres(geofence) {
    const polygons = normalizeGeofence(geofence);
    if (polygons.length === 0) return null;

    const allLats = polygons.flat().map(p => p.lat);
    if (allLats.length === 0) return null;
    const latMid = allLats.reduce((s, v) => s + v, 0) / allLats.length;
    const metersPerDegLat = 111320;
    const metersPerDegLng = 111320 * Math.cos((latMid * Math.PI) / 180);

    let sqMeters = 0;
    for (const poly of polygons) {
        const pts = poly.map(p => [p.lng * metersPerDegLng, p.lat * metersPerDegLat]);
        let area = 0;
        for (let i = 0; i < pts.length; i += 1) {
            const j = (i + 1) % pts.length;
            area += pts[i][0] * pts[j][1] - pts[j][0] * pts[i][1];
        }
        sqMeters += Math.abs(area) / 2;
    }
    return sqMeters > 0 ? sqMeters / 4046.86 : null;
}

/**
 * Centroid of all vertices across normalized geofence polygons.
 */
export function geofenceCentroid(geofence) {
    const polygons = normalizeGeofence(geofence);
    if (polygons.length === 0) return null;
    const allPoints = polygons.flat();
    if (allPoints.length === 0) return null;
    return {
        lat: allPoints.reduce((s, p) => s + p.lat, 0) / allPoints.length,
        lng: allPoints.reduce((s, p) => s + p.lng, 0) / allPoints.length,
    };
}

/**
 * Default ingress/egress slightly offset from geofence centroid, or property center
 * when no geofence exists (OSM failure). Keeps markers visibly separated.
 */
export function computeDefaultIngressEgress(geofence, propertyCenter) {
    let center = geofenceCentroid(geofence);
    if (!center
        && propertyCenter
        && typeof propertyCenter.lat === 'number'
        && typeof propertyCenter.lng === 'number') {
        center = { lat: propertyCenter.lat, lng: propertyCenter.lng };
    }
    if (!center) return { ingress: [], egress: [] };

    const polygons = normalizeGeofence(geofence);
    let offsetLat = 0.00008;
    let offsetLng = 0.00008;
    if (polygons.length > 0) {
        const allPoints = polygons.flat();
        const lats = allPoints.map(p => p.lat);
        const lngs = allPoints.map(p => p.lng);
        offsetLat = Math.max((Math.max(...lats) - Math.min(...lats)) * 0.05, 0.00005);
        offsetLng = Math.max((Math.max(...lngs) - Math.min(...lngs)) * 0.05, 0.00005);
    }

    return {
        ingress: [{ lat: center.lat + offsetLat, lng: center.lng - offsetLng }],
        egress: [{ lat: center.lat - offsetLat, lng: center.lng + offsetLng }],
    };
}
